import React, { useState, useEffect } from "react";
import Modal from "../ui/Modal";
import DepartmentSelector from "../selectors/DepartmentSelector";
import UserSelector from "../selectors/UserSelector";

export interface AddParticipantFormData {
  type: "USER" | "DEPARTMENT";
  selectedUsers: string[];
  selectedDepartments: string[];
}

interface AddParticipantModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (formData: AddParticipantFormData) => void;
  isLoading: boolean;
  title?: string;
  excludeUserIds?: string[];
  excludeDepartmentIds?: string[];
}

export default function AddParticipantModal({
  isOpen,
  onClose,
  onSubmit,
  isLoading,
  title = "Katılımcı Ekle",
  excludeUserIds = [],
  excludeDepartmentIds = [],
}: AddParticipantModalProps) {
  const [formData, setFormData] = useState<AddParticipantFormData>({
    type: "USER",
    selectedUsers: [],
    selectedDepartments: [],
  });

  const [selectedDepartmentDetails, setSelectedDepartmentDetails] = useState<Record<string, any>>({});
  const [selectedUserDetails, setSelectedUserDetails] = useState<Record<string, any>>({});

  const [currentSelectedUser, setCurrentSelectedUser] = useState<string>("");
  const [currentSelectedDepartment, setCurrentSelectedDepartment] = useState<string>("");

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  const resetForm = () => {
    setFormData({
      type: "USER",
      selectedUsers: [],
      selectedDepartments: [],
    });
    setSelectedDepartmentDetails({});
    setSelectedUserDetails({});
    setCurrentSelectedUser("");
    setCurrentSelectedDepartment("");
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  useEffect(() => {
    resetForm();
  }, [isOpen]);

  const handleTypeChange = (newType: "USER" | "DEPARTMENT") => {
    setFormData({
      type: newType,
      selectedUsers: [],
      selectedDepartments: [],
    });
    setSelectedDepartmentDetails({});
    setSelectedUserDetails({});
    setCurrentSelectedUser("");
    setCurrentSelectedDepartment("");
  };

  const handleUserAdd = (userId: string, user: any) => {
    if (userId && user && !formData.selectedUsers.includes(userId)) {
      setFormData((prev) => ({
        ...prev,
        selectedUsers: [...prev.selectedUsers, userId],
      }));
      setSelectedUserDetails((prev) => ({
        ...prev,
        [userId]: user,
      }));
      setCurrentSelectedUser("");
    }
  };

  const handleUserRemove = (userId: string) => {
    setFormData((prev) => ({
      ...prev,
      selectedUsers: prev.selectedUsers.filter((id) => id !== userId),
    }));
    setSelectedUserDetails((prev) => {
      const newDetails = { ...prev };
      delete newDetails[userId];
      return newDetails;
    });
  };

  const handleDepartmentAdd = (departmentId: string, department: any) => {
    if (departmentId && department && !formData.selectedDepartments.includes(departmentId)) {
      setFormData((prev) => ({
        ...prev,
        selectedDepartments: [...prev.selectedDepartments, departmentId],
      }));
      setSelectedDepartmentDetails((prev) => ({
        ...prev,
        [departmentId]: department,
      }));
      setCurrentSelectedDepartment("");
    }
  };

  const handleDepartmentRemove = (departmentId: string) => {
    setFormData((prev) => ({
      ...prev,
      selectedDepartments: prev.selectedDepartments.filter((id) => id !== departmentId),
    }));
    setSelectedDepartmentDetails((prev) => {
      const newDetails = { ...prev };
      delete newDetails[departmentId];
      return newDetails;
    });
  };

  const isFormValid = () => {
    if (formData.type === "USER") {
      return formData.selectedUsers.length > 0;
    } else {
      return formData.selectedDepartments.length > 0;
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title={title} size="lg">
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Participant Type Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">Katılımcı Tipi</label>
          <div className="flex gap-4">
            <label className="flex items-center">
              <input
                type="radio"
                name="type"
                value="USER"
                checked={formData.type === "USER"}
                onChange={(e) => handleTypeChange(e.target.value as "USER")}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
              />
              <span className="ml-2 text-sm text-gray-700">Kullanıcı</span>
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                name="type"
                value="DEPARTMENT"
                checked={formData.type === "DEPARTMENT"}
                onChange={(e) => handleTypeChange(e.target.value as "DEPARTMENT")}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
              />
              <span className="ml-2 text-sm text-gray-700">Departman</span>
            </label>
          </div>
        </div>

        {/* User Selection */}
        {formData.type === "USER" && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Kullanıcı Seçin (Çoklu Seçim Yapabilirsiniz)</label>

            {/* Selected Users Display */}
            {formData.selectedUsers.length > 0 && (
              <div className="mb-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div className="text-sm font-medium text-blue-900 mb-3">Seçili Kullanıcılar ({formData.selectedUsers.length})</div>
                <div className="flex flex-wrap gap-2">
                  {formData.selectedUsers.map((userId) => {
                    const user = selectedUserDetails[userId];
                    return user ? (
                      <span
                        key={userId}
                        className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-100 text-blue-800 rounded-full text-sm">
                        {`${user.firstName || ""} ${user.lastName || ""}`.trim() || "İsimsiz Kullanıcı"}
                        <button
                          type="button"
                          onClick={() => handleUserRemove(userId)}
                          className="text-blue-600 hover:text-blue-800 font-bold">
                          ×
                        </button>
                      </span>
                    ) : null;
                  })}
                </div>
              </div>
            )}

            {/* User Selector */}
            <UserSelector
              value={currentSelectedUser}
              onChange={handleUserAdd}
              placeholder="Kullanıcı Seçin"
              excludeIds={[...formData.selectedUsers, ...excludeUserIds]}
              allowClear={true}
              required={false}
              inline={true}
              autoOpen={true}
              hideSelected={true}
              className="text-sm"
            />
          </div>
        )}

        {/* Department Selection */}
        {formData.type === "DEPARTMENT" && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Departman Seçin (Çoklu Seçim Yapabilirsiniz)</label>

            {/* Selected Departments Display */}
            {formData.selectedDepartments.length > 0 && (
              <div className="mb-4 p-4 bg-green-50 rounded-lg border border-green-200">
                <div className="text-sm font-medium text-green-900 mb-3">Seçili Departmanlar ({formData.selectedDepartments.length})</div>
                <div className="flex flex-wrap gap-2">
                  {formData.selectedDepartments.map((deptId) => {
                    const dept = selectedDepartmentDetails[deptId];
                    return dept ? (
                      <span
                        key={deptId}
                        className="inline-flex items-center gap-2 px-3 py-1.5 bg-green-100 text-green-800 rounded-full text-sm">
                        {dept.color && <div className="w-3 h-3 rounded-full" style={{ backgroundColor: dept.color }} />}
                        {dept.name}
                        <button
                          type="button"
                          onClick={() => handleDepartmentRemove(deptId)}
                          className="text-green-600 hover:text-green-800 font-bold">
                          ×
                        </button>
                      </span>
                    ) : null;
                  })}
                </div>
              </div>
            )}

            {/* Department Selector */}
            <DepartmentSelector
              value={currentSelectedDepartment}
              onChange={handleDepartmentAdd}
              placeholder="Departman Seçin"
              excludeIds={[...formData.selectedDepartments, ...excludeDepartmentIds]}
              allowClear={false}
              required={false}
              showColors={true}
              inline={true}
              className="text-sm"
            />
          </div>
        )}

        {/* Form Actions */}
        <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
          <button
            type="button"
            onClick={handleClose}
            disabled={isLoading}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 disabled:opacity-50 font-medium">
            İptal
          </button>
          <button
            type="submit"
            disabled={!isFormValid() || isLoading}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 font-medium flex items-center gap-2">
            {isLoading && <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>}
            {isLoading ? "Ekleniyor..." : "Ekle"}
          </button>
        </div>
      </form>
    </Modal>
  );
}
